import { useSession } from 'hooks';
import { sortNames } from 'logic/sortNames';
import { Permission } from 'shared/permissions';
import { AugmentedDevice, BillingService, BuildingOrg, Contract, ContractId, ContractType, DeviceClass, DeviceId, OrgId } from 'shared/models';
import { BillingServiceName, BillingStrings } from 'ui/contracts/contracts';
import { ErrorMessage, WarningMessage } from 'ui/shared/messages';
import { BuiltInOrgIds } from 'shared/orgs';

require('./BillingWarnings.css');

export type Props = {
    unbilledServices: readonly BillingService[];
    overbilledServices: readonly BillingService[];
};

const isContractActive = (service: BillingService, contract?: Contract) =>
    contract?.services[service]
    && (
        contract.type === ContractType.Monthly
        || contract.expiryDate! > Date.now()
    );

export const getBillingServicesWarnings = (
    buildingOrgs: Record<OrgId, BuildingOrg>,
    contracts: Record<ContractId, Contract>,
    isEcs: boolean,
    devices: Record<DeviceId, AugmentedDevice>,
    isLoading: boolean,
) => {
    const overbilledServices: BillingService[] = [];
    const unbilledServices: BillingService[] = [];

    const hasSimCard = Object.values(devices).some(d => d.deviceClass === DeviceClass.SIMCard);
    const hasAtaDevice = Object.values(devices).some(d => d.deviceClass === DeviceClass.ATA);

    const serviceChecks: Record<BillingService, { condition: boolean }> = {
        [BillingService.Data]: {
            condition: hasSimCard,
        },
        [BillingService.RemoteAccess]: {
            condition: isEcs,
        },
        [BillingService.VOIP]: {
            condition: hasAtaDevice,
        },
        [BillingService.PhoneAnswering]: {
            condition: isEcs,
        },
        [BillingService.Insights]: {
            condition: false, // Nobody has Insights yet... isn't released as a product yet.
        },
    };

    if (!isLoading) {
        for (const [ key, { condition } ] of Object.entries(serviceChecks)) {
            const billingService = key as BillingService;
            const activeContracts = Object.values(buildingOrgs)
                .map(bo => contracts[bo.contractId!])
                .filter(contract => isContractActive(billingService, contract));

            if (condition && !activeContracts.length) {
                unbilledServices.push(billingService);
            }
            else if ((condition && activeContracts.length > 1) || (!condition && activeContracts.length)) {
                overbilledServices.push(billingService);
            }
        }
    }

    return { unbilledServices, overbilledServices };
};

export const BillingWarnings = ({ unbilledServices, overbilledServices }: Props) => {
    const { hasPermission } = useSession();

    if (!hasPermission(null, Permission.Admin) || (!unbilledServices.length && !overbilledServices.length)) {
        return null;
    }

    const unbilledServiceNames = unbilledServices.map(bs => BillingServiceName[bs]).sort(sortNames).join(', ');
    const overbilledServiceNames = overbilledServices.map(bs => BillingServiceName[bs]).sort(sortNames).join(', ');

    return (
        <div className="billing-warnings">
            { unbilledServices.length > 0 &&
                <WarningMessage
                    message={
                        <>
                            { BillingStrings.UnbilledServices }<b>{ unbilledServiceNames }</b>
                        </>
                    }
                />
            }
            { overbilledServices.length > 0 &&
                <ErrorMessage
                    message={
                        <>
                            { BillingStrings.OverilledServices }<b>{ overbilledServiceNames }</b>
                        </>
                    }
                />
            }
        </div>
    );
};
